import { createSelector } from '@ngrx/store';
import { SeatKind, TellentApps } from '@tellent/types';

import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
} from '@recruitee/ngrx';

import { EntityStoreKey } from '../types';

const idSelector = ({ guid }: { guid: string }) => guid;

const APPS_ORDER = [SeatKind.Recruitment, SeatKind.CoreHR];

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TellentApps>()(
  EntityStoreKey.TellentApps,
  {
    idSelector,
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        fetchCollectionSuccess: (state, payload: { data: TellentApps[] }) => {
          const payloadWithSortedApps = {
            ...payload,
            data: [...payload.data].sort(
              (a, b) => APPS_ORDER.indexOf(a.kind) - APPS_ORDER.indexOf(b.kind),
            ),
          };

          return defaultActions.fetchCollectionSuccess(state, payloadWithSortedApps);
        },
      };
    },
  },
);

const appsTypeSelector = createSelector(selectors.entities(), (data): SeatKind[] =>
  data.map(({ kind }) => kind),
);

export const selectRolesSelectOptions = memoizeSelector((seatKind: SeatKind) =>
  createSelector(
    selectAppBySeatKind(seatKind),
    app =>
      app?.roles.map(({ externalId, name }) => ({
        label: name,
        value: externalId,
      })) || [],
  ),
);

export const selectDefaultRoleBySeatKind = memoizeSelector((seatKind: SeatKind) =>
  createSelector(
    selectAppBySeatKind(seatKind),
    app => app?.roles.find(role => role.default) || app?.roles[0],
  ),
);

export const selectAppBySeatKind = memoizeSelector((seatKind: SeatKind) => {
  const selector = selectors.entities();
  return createSelector(selector, apps => apps.find(app => app.kind === seatKind));
});

const TellentAppsSelectors = {
  ...selectors,
  selectRolesSelectOptions,
  selectAppBySeatKind,
  selectDefaultRoleBySeatKind,
  appsTypeSelector,
};

export {
  actions as TellentAppsActions,
  TellentAppsSelectors,
  reducer as TellentAppsReducer,
  namespace as TellentAppsNamespace,
};
